// Top-of-page summary for the War Room scorecard: verdict chips (count + monthly
// pool), Buy Box holder tally (regional crawler, most ZIPs), unit totals, scorecard date, and the two action
// buttons (Refresh / Download). Purely presentational — all data comes from
// scorecard.summary (Task 4's ScorecardService.buildSummary) plus scorecardDate.
import React from 'react';

const VERDICT_ORDER = ['ENFORCE-MAP', 'DEFEND', 'CONTEST', 'PRICE', 'FLIP', 'SPREAD', 'HOLD', 'REVIEW'];

const VERDICT_STYLES = {
    'ENFORCE-MAP': 'bg-amber-100 text-amber-800 border-amber-300',
    'DEFEND': 'bg-red-100 text-red-800 border-red-300',
    'CONTEST': 'bg-blue-100 text-blue-800 border-blue-300',
    'PRICE': 'bg-rose-100 text-rose-800 border-rose-300',
    'FLIP': 'bg-green-100 text-green-800 border-green-300',
    'SPREAD': 'bg-purple-100 text-purple-800 border-purple-300',
    'HOLD': 'bg-gray-100 text-gray-800 border-gray-300',
    'REVIEW': 'bg-slate-100 text-slate-800 border-slate-300',
};

const fmtMoney = (v) => `$${Math.round(v || 0).toLocaleString()}`;

function Spinner() {
    return (
        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
    );
}

function SummaryStrip({ summary, scorecardDate, refreshing, onRefresh, onDownload }) {
    const verdictCounts = summary?.verdictCounts || {};
    const verdictPools = summary?.verdictPools || {};
    const natlTally = summary?.natlTally || { us: 0, mp: 0, other: 0, none: 0 };
    const ourUnitsTotal = summary?.ourUnitsTotal ?? 0;
    const mpUnitsTotal = summary?.mpUnitsTotal ?? 0;

    return (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <span className="text-sm text-gray-500">Scorecard date: </span>
                    <span className="text-sm font-semibold text-gray-900">{scorecardDate || '—'}</span>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onRefresh}
                        disabled={refreshing}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {refreshing && <Spinner />}
                        {refreshing ? 'Refreshing…' : 'Refresh'}
                    </button>
                    <button
                        type="button"
                        onClick={onDownload}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 text-sm font-semibold rounded-md border border-gray-300 hover:bg-gray-50"
                    >
                        Download CSV
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-2">
                {VERDICT_ORDER.map((v) => (
                    <span
                        key={v}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${VERDICT_STYLES[v]}`}
                    >
                        {v} &middot; {verdictCounts[v] || 0} &middot; {fmtMoney(verdictPools[v])}/mo
                    </span>
                ))}
            </div>

            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600 border-t border-gray-100 pt-3">
                <div title="Who holds the Buy Box for regular shoppers: the seller winning it in the most of the regional crawler's 50 ZIPs on its latest day (within 7 days). Our Buy Box share comes from Amazon's Business Report. Unknown = the crawler has no recent check.">
                    <span className="text-gray-500">BB holder (most ZIPs) — </span>
                    <span className="font-medium text-gray-900">US {natlTally.us}</span>
                    <span className="mx-1 text-gray-300">/</span>
                    <span className="font-medium text-gray-900">MP {natlTally.mp}</span>
                    <span className="mx-1 text-gray-300">/</span>
                    <span className="font-medium text-gray-900">Other {natlTally.other}</span>
                    <span className="mx-1 text-gray-300">/</span>
                    <span className="font-medium text-gray-900">Unknown {natlTally.none}</span>
                </div>
                <div>
                    <span className="text-gray-500">Our units: </span>
                    <span className="font-medium text-gray-900">{ourUnitsTotal.toLocaleString()}</span>
                </div>
                <div>
                    <span className="text-gray-500">MP units: </span>
                    <span className="font-medium text-gray-900">{mpUnitsTotal.toLocaleString()}</span>
                </div>
            </div>
        </div>
    );
}

export default SummaryStrip;
